import { useCallback, useEffect, useRef } from 'react';
import { IFrameMessage } from 'src/model/enum/enums';

interface MessageHandler {
    id: string;
    handler: (event: MessageEvent) => void;
    types?: IFrameMessage[];
}

class GlobalMessageListener {
    private static instance: GlobalMessageListener;
    private handlers: Map<string, MessageHandler> = new Map();
    private isListening: boolean = false;

    private constructor() { }

    static getInstance(): GlobalMessageListener {
        if (!GlobalMessageListener.instance) {
            GlobalMessageListener.instance = new GlobalMessageListener();
        }
        return GlobalMessageListener.instance;
    }

    private handleMessage = (event: MessageEvent) => {
        this.handlers.forEach((handler) => {
            // If types are specified, only call handler for those types
            if (handler.types && handler.types.length > 0) {
                if (handler.types.includes(event.data.type)) {
                    handler.handler(event);
                }
            } else {
                // If no types specified, call handler for all messages
                handler.handler(event);
            }
        });
    };

    addHandler(id: string, handler: (event: MessageEvent) => void, types?: IFrameMessage[]): void {
        this.handlers.set(id, { id, handler, types });

        if (!this.isListening) {
            window.addEventListener('message', this.handleMessage);
            this.isListening = true;
        }
    }

    removeHandler(id: string): void {
        this.handlers.delete(id);

        if (this.handlers.size === 0 && this.isListening) {
            window.removeEventListener('message', this.handleMessage);
            this.isListening = false;
        }
    }

    getHandlerCount(): number {
        return this.handlers.size;
    }
}

export const useGlobalMessageListener = (
    handler: (event: MessageEvent) => void,
    types?: IFrameMessage[]
) => {
    const listener = useRef(GlobalMessageListener.getInstance());
    const handlerId = useRef(`handler-${Math.random().toString(36).substr(2, 9)}`);

    const addHandler = useCallback(() => {
        listener.current.addHandler(handlerId.current, handler, types);
    }, [handler, types]);

    const removeHandler = useCallback(() => {
        listener.current.removeHandler(handlerId.current);
    }, []);

    useEffect(() => {
        addHandler();
        return removeHandler;
    }, [addHandler, removeHandler]);

    return {
        addHandler,
        removeHandler,
        getHandlerCount: () => listener.current.getHandlerCount()
    };
}; 